import React, { useState } from 'react'
import classnames from 'classnames'
import PropTypes from 'prop-types'

import { IconButton } from '@st-operations/uikit/src/v2/index'

import AggregationConfiguration from './aggregation-configuration/index.jsx'
import FilterConfiguration from './filter-configuration/index.jsx'
import AttributeConfiguration from './attribute-configuration/index.jsx'
import CustomAttributeConfiguration from './custom-attribute-configuration/index.jsx'
import ClosePanelButton from '_components/close-panel-button/index.jsx'

import './styles.scss'
import EventFeedsQueryOnboardTooltips from '../query-onboard-tooltips/index.jsx'
import { useEffect } from 'react'
import { useEventFeedsContext } from '../context/index.jsx'
import { TYPE_PREBUILT_ATTRIBUTES_V2 } from '../wizard/index.jsx'
import CustomTaxonomyConfiguration from './custom-taxonomy-configuration/index.jsx'

const TABS = {
  QUERY: 'Query',
}

const QueryPanel = ({
  className,
  panelWidth,
  isCollapsed,
  onOpenPanel,
  onClosePanel,
  queryOnboardShown,
  onQueryOnboardFinish,
}) => {
  const feedsContext = useEventFeedsContext()
  const {
    data: { type },
    permissions,
  } = feedsContext
  const [filtersRef, setFiltersRef] = useState()
  const [aggregateByRef, setAggregateByRef] = useState()
  const [attributesRef, setAttributesRef] = useState()
  const [customAttributesRef, setCustomAttributesRef] = useState()
  const [contentRef, setContentRef] = useState()
  const [selectedTab, setSelectedTab] = useState(TABS.QUERY)
  const [scrollTop, setScrollTop] = useState(0)

  const predictors = type === TYPE_PREBUILT_ATTRIBUTES_V2

  const handleOpenPanel = (clickedButtonName) => () => {
    setSelectedTab(clickedButtonName)
    onOpenPanel()
  }

  useEffect(() => {
    if (contentRef) {
      contentRef.addEventListener('scroll', (event) => {
        setScrollTop(event.target.scrollTop)
      })
    }
  }, [contentRef])

  const handleQueryOnboardFinish = (viewedAllSteps) => {
    onQueryOnboardFinish(viewedAllSteps)
  }

  if (!permissions) {
    return null
  }

  const hasPermission = (item) => {
    const config = item === 'custom_taxonomy' || item === 'custom_label' ? 'attributes' : item
    return permissions?.configuration?.[config]?.display
  }

  const items = {
    filters: !predictors && <FilterConfiguration ref={(ref) => setFiltersRef(ref)} />,
    aggregation: !predictors && <AggregationConfiguration ref={(ref) => setAggregateByRef(ref)} />,
    attributes: <AttributeConfiguration ref={(ref) => setAttributesRef(ref)} />,
    custom_attributes: !predictors && (
      <CustomAttributeConfiguration ref={(ref) => setCustomAttributesRef(ref)} />
    ),
    custom_taxonomy: !predictors && <CustomTaxonomyConfiguration />,
  }

  return (
    <section
      className={classnames('query-panel', { 'query-panel--collapsed': isCollapsed }, className)}
    >
      <Choose>
        <When condition={isCollapsed}>
          <IconButton
            iconProps={{
              icon: 'faders',
              type: 'outline',
              color: selectedTab === TABS.QUERY ? '#13682B' : 'currentColor',
              width: 24,
              height: 24,
            }}
            className={classnames(
              { ['query-panel__icon-selected']: selectedTab === TABS.QUERY },
              'query-panel__query-icon'
            )}
            onClick={handleOpenPanel(TABS.QUERY)}
            ariaLabel="Open query panel"
            text={TABS.QUERY}
            tooltipDirection="left"
          />
        </When>
        <Otherwise>
          <div ref={(ref) => setContentRef(ref)} className="query-panel__content">
            <Choose>
              <When condition={selectedTab === TABS.QUERY}>
                {Object.keys(items).map((item) => {
                  if (hasPermission(item)) {
                    return <div key={item}>{items[item]}</div>
                  }
                })}
              </When>
              <Otherwise>{null}</Otherwise>
            </Choose>
          </div>
          <ClosePanelButton className="query-panel__close" onClose={onClosePanel} />
        </Otherwise>
      </Choose>
      <If condition={queryOnboardShown}>
        <EventFeedsQueryOnboardTooltips
          scrollRef={contentRef}
          filtersRef={filtersRef}
          aggregateByRef={aggregateByRef}
          attributesRef={attributesRef}
          customAttributesRef={customAttributesRef}
          scrollTop={scrollTop}
          panelWidth={panelWidth}
          isCollapsed={isCollapsed}
          onFinish={handleQueryOnboardFinish}
        />
      </If>
    </section>
  )
}

QueryPanel.propTypes = {
  className: PropTypes.string,
  isCollapsed: PropTypes.bool,
  onOpenPanel: PropTypes.func,
  onClosePanel: PropTypes.func,
}

QueryPanel.defaultProps = {
  className: '',
  isCollapsed: false,
  onOpenPanel: () => { },
  onClosePanel: () => { },
}

export default QueryPanel
